const modalId = 'festfriends-confirmation-modal';

let pendingAction = null;
let previouslyFocusedElement = null;
let initializedModal = null;
let isProcessing = false;

function getModal() {
    return document.getElementById(modalId);
}

function getFocusableElements(modal) {
    return Array.from(
        modal.querySelectorAll(
            [
                'button:not([disabled])',
                'a[href]',
                'input:not([disabled])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                '[tabindex]:not([tabindex="-1"])',
            ].join(','),
        ),
    ).filter((element) => {
        return (
            element instanceof HTMLElement &&
            !element.hidden &&
            element.offsetParent !== null
        );
    });
}

function setButtonText(modal, text) {
    const textElement = modal.querySelector(
        '[data-confirm-button-text]',
    );

    if (textElement) {
        textElement.textContent = text;
    }
}

function setProcessingState(modal, processing) {
    const acceptButton = modal.querySelector(
        '[data-confirm-accept]',
    );

    const cancelButton = modal.querySelector(
        '[data-confirm-cancel]',
    );

    const closeButton = modal.querySelector(
        '[data-confirm-close]',
    );

    const spinner = modal.querySelector(
        '[data-confirm-spinner]',
    );

    isProcessing = processing;

    if (acceptButton) {
        acceptButton.disabled = processing;
        acceptButton.setAttribute(
            'aria-busy',
            processing ? 'true' : 'false',
        );
    }

    if (cancelButton) {
        cancelButton.disabled = processing;
    }

    if (closeButton) {
        closeButton.disabled = processing;
    }

    spinner?.classList.toggle('hidden', !processing);
}

function configureModal(trigger) {
    const modal = getModal();

    if (!modal) {
        return false;
    }

    const title =
        trigger.dataset.confirmTitle?.trim() ||
        'Confirm action';

    const message =
        trigger.dataset.confirmMessage?.trim() ||
        'Are you sure you want to continue?';

    const buttonText =
        trigger.dataset.confirmButton?.trim() ||
        'Continue';

    const cancelText =
        trigger.dataset.confirmCancel?.trim() ||
        'Cancel';

    const processingText =
        trigger.dataset.confirmProcessing?.trim() ||
        'Processing…';

    const allowedVariants = [
        'danger',
        'warning',
        'success',
        'neutral',
    ];

    const requestedVariant =
        trigger.dataset.confirmVariant?.trim() ||
        'neutral';

    const variant = allowedVariants.includes(
        requestedVariant,
    )
        ? requestedVariant
        : 'neutral';

    modal.dataset.confirmVariant = variant;
    modal.dataset.processingText = processingText;
    modal.dataset.buttonText = buttonText;

    const titleElement = modal.querySelector(
        '[data-confirm-title]',
    );

    const messageElement = modal.querySelector(
        '[data-confirm-message]',
    );

    const cancelButton = modal.querySelector(
        '[data-confirm-cancel]',
    );

    if (titleElement) {
        titleElement.textContent = title;
    }

    if (messageElement) {
        messageElement.textContent = message;
    }

    if (cancelButton) {
        cancelButton.textContent = cancelText;
    }

    setButtonText(modal, buttonText);
    setProcessingState(modal, false);

    modal
        .querySelectorAll('[data-confirm-icon]')
        .forEach((icon) => {
            icon.classList.toggle(
                'hidden',
                icon.dataset.confirmIcon !== variant,
            );
        });

    return true;
}

function openModal(trigger, action) {
    const modal = getModal();

    if (!modal || !configureModal(trigger)) {
        action();
        return;
    }

    pendingAction = action;
    previouslyFocusedElement = document.activeElement;

    modal.classList.remove(
        'is-closing',
        'is-visible',
    );

    if (!modal.open) {
        modal.showModal();
    }

    window.requestAnimationFrame(() => {
        modal.classList.add('is-visible');

        const cancelButton = modal.querySelector(
            '[data-confirm-cancel]',
        );

        cancelButton?.focus();
    });
}

function restorePreviousFocus() {
    if (
        previouslyFocusedElement instanceof HTMLElement &&
        document.contains(previouslyFocusedElement)
    ) {
        previouslyFocusedElement.focus();
    }

    previouslyFocusedElement = null;
}

function finishClosingModal(modal) {
    if (modal.open) {
        modal.close();
    }

    modal.classList.remove(
        'is-visible',
        'is-closing',
    );

    pendingAction = null;
    setProcessingState(modal, false);
    restorePreviousFocus();
}

function closeModal({ immediate = false } = {}) {
    const modal = getModal();

    if (!modal || !modal.open || isProcessing) {
        return;
    }

    if (immediate) {
        finishClosingModal(modal);
        return;
    }

    modal.classList.remove('is-visible');
    modal.classList.add('is-closing');

    window.setTimeout(() => {
        finishClosingModal(modal);
    }, 180);
}

function permanentlyDisableSubmitter(submitter) {
    if (
        !(
            submitter instanceof HTMLButtonElement ||
            submitter instanceof HTMLInputElement
        )
    ) {
        return;
    }

    submitter.disabled = true;
    submitter.setAttribute('aria-disabled', 'true');

    if (submitter instanceof HTMLButtonElement) {
        submitter.dataset.originalText =
            submitter.textContent?.trim() || '';

        submitter.textContent =
            submitter.dataset.confirmProcessing?.trim() ||
            'Processing…';
    }
}

function submitForm(form, submitter) {
    form.dataset.confirmedSubmission = 'true';

    /*
    |--------------------------------------------------------------------------
    | Preserve the original submit button's name/value
    |--------------------------------------------------------------------------
    |
    | A confirmation modal delays the real form submission. Some browsers can
    | lose the submitter's name/value during that handoff, which is significant
    | for forms with multiple submit actions such as "Save Draft" versus
    | "Submit for Review". Mirror the clicked submitter into a temporary hidden
    | input so the server always receives the intended action.
    |
    */

    let submitterMirror = null;

    if (
        submitter instanceof HTMLButtonElement
        || submitter instanceof HTMLInputElement
    ) {
        const name =
            submitter.name?.trim();

        if (name) {
            submitterMirror =
                document.createElement('input');

            submitterMirror.type =
                'hidden';

            submitterMirror.name =
                name;

            submitterMirror.value =
                submitter.value;

            submitterMirror.dataset
                .confirmSubmitterMirror =
                    'true';

            form.appendChild(
                submitterMirror
            );
        }
    }

    if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(
            submitter || undefined
        );
        return;
    }

    form.dataset.submitting = 'true';
    permanentlyDisableSubmitter(submitter);
    form.submit();
}

function continueAction() {
    const modal = getModal();
    const action = pendingAction;

    if (!modal || !action || isProcessing) {
        return;
    }

    setProcessingState(modal, true);

    setButtonText(
        modal,
        modal.dataset.processingText || 'Processing…',
    );

    pendingAction = null;

    window.setTimeout(() => {
        action();
    }, 120);
}

function handleClick(event) {
    const trigger = event.target.closest(
        'a[data-confirm], button[data-confirm], input[data-confirm]',
    );

    if (!trigger) {
        return;
    }

    if (
        trigger.disabled ||
        trigger.getAttribute('aria-disabled') === 'true'
    ) {
        event.preventDefault();
        return;
    }

    const form = trigger.closest('form');

    if (
        form &&
        form.dataset.submitting === 'true'
    ) {
        event.preventDefault();
        return;
    }

    if (form) {
        event.preventDefault();

        openModal(trigger, () => {
            submitForm(form, trigger);
        });

        return;
    }

    if (trigger instanceof HTMLAnchorElement) {
        const url = trigger.href;

        if (!url) {
            return;
        }

        event.preventDefault();

        openModal(trigger, () => {
            trigger.setAttribute(
                'aria-disabled',
                'true',
            );

            window.location.assign(url);
        });
    }
}

function handleSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
        return;
    }

    /*
    |--------------------------------------------------------------------------
    | Confirmed modal submission
    |--------------------------------------------------------------------------
    |
    | requestSubmit() triggers this handler a second time. Allow that second
    | submission to continue, then lock the form against further submissions.
    |
    */

    if (form.dataset.confirmedSubmission === 'true') {
        delete form.dataset.confirmedSubmission;

        form.dataset.submitting = 'true';

        permanentlyDisableSubmitter(event.submitter);

        /*
         * Leave any temporary submitter mirror in place for this submission.
         * Navigation will discard it with the page.
         */

        return;
    }

    if (form.dataset.submitting === 'true') {
        event.preventDefault();
        return;
    }

    const submitter = event.submitter;

    const confirmationSource =
        submitter?.matches('[data-confirm]')
            ? submitter
            : form.matches('[data-confirm]')
                ? form
                : null;

    if (!confirmationSource) {
        return;
    }

    event.preventDefault();

    openModal(confirmationSource, () => {
        submitForm(form, submitter);
    });
}

function handleModalKeydown(event) {
    const modal = getModal();

    if (!modal?.open) {
        return;
    }

    if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
    }

    if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !(event.target instanceof HTMLTextAreaElement)
    ) {
        event.preventDefault();
        continueAction();
        return;
    }

    if (event.key !== 'Tab') {
        return;
    }

    const focusableElements =
        getFocusableElements(modal);

    if (focusableElements.length === 0) {
        event.preventDefault();
        return;
    }

    const firstElement = focusableElements[0];
    const lastElement =
        focusableElements[
            focusableElements.length - 1
        ];

    if (
        event.shiftKey &&
        document.activeElement === firstElement
    ) {
        event.preventDefault();
        lastElement.focus();
        return;
    }

    if (
        !event.shiftKey &&
        document.activeElement === lastElement
    ) {
        event.preventDefault();
        firstElement.focus();
    }
}

function initializeModal() {
    const modal = getModal();

    if (!modal || initializedModal === modal) {
        return;
    }

    initializedModal = modal;

    modal
        .querySelector('[data-confirm-cancel]')
        ?.addEventListener('click', () => {
            closeModal();
        });

    modal
        .querySelector('[data-confirm-close]')
        ?.addEventListener('click', () => {
            closeModal();
        });

    modal
        .querySelector('[data-confirm-accept]')
        ?.addEventListener(
            'click',
            continueAction,
        );

    modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeModal();
    });

    modal.addEventListener('click', (event) => {
        if (event.target === modal) {
            closeModal();
        }
    });

    modal
        .querySelector('[data-confirm-panel]')
        ?.addEventListener('click', (event) => {
            event.stopPropagation();
        });

    modal.addEventListener(
        'keydown',
        handleModalKeydown,
    );
}

document.addEventListener('click', handleClick);
document.addEventListener('submit', handleSubmit);

document.addEventListener(
    'DOMContentLoaded',
    initializeModal,
);

document.addEventListener(
    'livewire:navigated',
    initializeModal,
);
document.addEventListener(
    'festfriends:confirmation-complete',
    () => {
        const modal = getModal();

        if (!modal?.open) {
            return;
        }

        finishClosingModal(modal);
    }
);
initializeModal();